import { useState } from "react";
import CardIcon, { CardFlag, cardIconByScheme } from "@/shared/ui/components/card-icon";
import { resolveScheme } from "@/lib/card";
import { CardScheme, PaymentMethod } from "@/types";
import { PaymentMethodUtils } from "../services/payment-method-utils";
import { PAYMENT_METHOD_TYPE, type PaymentMethodOption } from "../types";
import type { PpsPaymentMethodDetails } from "@/modules/checkout-session";
import { encodeCardScheme } from "@/lib/codec";
import {
  emitOverduePaymentsTracking,
  emitPaymentMethodSelectionTracking,
} from "@/shared/tracking/analytics";
import type { CheckoutSession } from "@/types";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NewCardIcon } from "@/shared/ui/icons/new-card";

interface PaymentMethodListProps {
  paymentMethods: PaymentMethodOption[];
  selectedMethod?: PpsPaymentMethodDetails;
  onMethodSelect: (method: PaymentMethodOption | "new-card") => void;
  rtl?: boolean;
  session?: CheckoutSession;
  /** When set to overdue-payments, emits payment_overdues__* analytics events. */
  trackingScope?: "default" | "overdue-payments";
}

/**
 * Component for displaying list of payment methods
 * Single responsibility: Rendering payment method list UI
 */
export function PaymentMethodList({
  paymentMethods,
  selectedMethod,
  onMethodSelect,
  rtl,
  session,
  trackingScope = "default",
}: PaymentMethodListProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleMethodClick = (method: PaymentMethodOption | "new-card") => {
    if (method === "new-card") {
      if (session && trackingScope === "overdue-payments") {
        emitOverduePaymentsTracking(
          "payment_overdues__payment_option_list_add_new_card_clicked",
          session,
        );
      }
      onMethodSelect("new-card");
      return;
    }

    if (session) {
      if (trackingScope === "overdue-payments") {
        if (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
          emitOverduePaymentsTracking(
            "payment_overdues__payment_option_list_apple_pay_clicked",
            session,
          );
        } else if (method.card) {
          emitOverduePaymentsTracking(
            "payment_overdues__payment_option_list_existing_card_clicked",
            session,
            {
              card_id: method.card.id,
              last4: method.card.last4,
              expiry_month: method.card.expiryMonth,
              expiry_year: method.card.expiryYear,
            },
          );
        }
      } else {
        if (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
          emitPaymentMethodSelectionTracking("apple_pay_selected", session, {
            payment_method_type: "apple_pay",
          });
        } else if (method.card) {
          emitPaymentMethodSelectionTracking("payment_method_selected", session, {
            payment_method_type: "card",
            card_id: method.card.id,
            card_scheme:
              encodeCardScheme(method.card.scheme ?? CardScheme.UNKNOWN) ||
              undefined,
            card_last4: method.card.last4 ?? undefined,
            card_is_debit: method.card.isDebit,
            card_is_prepaid: method.card.isPrepaid,
            card_is_auto_charge: method.card.isAutoCharge,
          });
        }
      }
    }

    onMethodSelect(method);
  };

  const isNewCardSelected = selectedMethod?.id === "new";

  const selectedMethodOption = paymentMethods.find((method) =>
    PaymentMethodUtils.isPaymentMethodSelected(method, selectedMethod),
  );

  const getPaymentMethodIcon = (method: PaymentMethodOption) => {
    if (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
      return <CardIcon type={CardFlag.APPLE_PAY} />;
    }

    if (method.card?.paymentMethod === PaymentMethod.MADA_CARD) {
      return <CardIcon type={CardFlag.MADA} />;
    }

    return cardIconByScheme(
      method.card ? resolveScheme(method.card) : CardScheme.UNKNOWN,
    );
  };

  const getPaymentMethodName = (method: PaymentMethodOption) => {
    return PaymentMethodUtils.getPaymentMethodName(method);
  };

  const getPaymentMethodDetails = (method: PaymentMethodOption): string => {
    if (method.paymentMethodType === PAYMENT_METHOD_TYPE.APPLE) {
      return method.card?.last4 ?? "";
    }
    const last4 = method.card?.last4;
    return last4 ? `**** ${last4}` : "";
  };

  const caretColor = "#A3A3A3";

  const renderPaymentMethodRow = (
    method: PaymentMethodOption,
    isSelected: boolean,
    onClick?: () => void,
  ) => (
    <div
      className="py-2 px-1 flex items-center cursor-pointer justify-between"
      data-testid={`payment-method-row-${method.card?.id ?? method.paymentMethodType}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4">
        <div className="w-12 flex items-center justify-center shrink-0">
          {getPaymentMethodIcon(method)}
        </div>
        <div
          className="text-sm font-semibold text-[#16181D]"
          style={{ textAlign: rtl ? "right" : "left" }}
        >
          <div>{getPaymentMethodName(method)}</div>
          {getPaymentMethodDetails(method) && (
            <div className="text-sm text-[#585B7A] font-medium">
              {getPaymentMethodDetails(method)}
            </div>
          )}
        </div>
      </div>
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          isSelected
            ? "border-purple-600 bg-purple-600"
            : "border-gray-300"
        }`}
        data-testid={`payment-method-radio-${method.card?.id ?? method.paymentMethodType}`}
      >
        {isSelected && (
          <div className="w-full h-full rounded-full bg-white scale-50"></div>
        )}
      </div>
    </div>
  );

  const renderNewCardRow = () => (
    <div
      className="py-2 px-1 flex items-center cursor-pointer justify-between"
      data-testid="payment-method-row-new-card"
      onClick={() => handleMethodClick("new-card")}
    >
      <div className="flex items-center gap-4">
        <div className="w-12 flex items-center justify-center shrink-0">
          <NewCardIcon />
        </div>
        <div className="text-sm font-semibold text-[#16181D]">
          {t("payment_confirmation.add_new_card")}
        </div>
      </div>
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
          isNewCardSelected
            ? "border-purple-600 bg-purple-600"
            : "border-gray-300"
        }`}
        data-testid="payment-method-radio-new-card"
      >
        {isNewCardSelected && (
          <div className="w-full h-full rounded-full bg-white scale-50"></div>
        )}
      </div>
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-[20px] p-4" data-testid="payment-method-list-container">
      {/* Content container using CSS Grid for crossfade effect */}
      <div className="grid">
        {/* Collapsed State - Selected Payment Method */}
        <div
          className="col-start-1 row-start-1 transition-opacity duration-300 ease-in-out"
          style={{
            opacity: isExpanded ? 0 : 1,
            pointerEvents: isExpanded ? "none" : "auto",
          }}
        >
          {selectedMethodOption && renderPaymentMethodRow(selectedMethodOption, true)}
        </div>

        {/* Expanded Content */}
        <div
          className="col-start-1 row-start-1 transition-all duration-300 ease-in-out"
          style={{
            opacity: isExpanded ? 1 : 0,
            maxHeight: isExpanded ? "500px" : "0px",
            overflow: "hidden",
            pointerEvents: isExpanded ? "auto" : "none",
          }}
        >
          <div>
            {paymentMethods.map((method) => {
              const isSelected = PaymentMethodUtils.isPaymentMethodSelected(
                method,
                selectedMethod,
              );

              return (
                <div key={method.card?.id ?? "apple-pay"}>
                  {renderPaymentMethodRow(method, isSelected, () =>
                    handleMethodClick(method)
                  )}
                </div>
              );
            })}
            {renderNewCardRow()}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 my-4"></div>
      <div
        className="flex justify-between items-center cursor-pointer py-1"
        data-testid="payment-method-toggle-expand"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="text-sm">
          {isExpanded
            ? t("payment_confirmation.confirm")
            : t("payment_confirmation.change")}
        </div>
        <div className="flex items-center" style={{ color: caretColor }}>
          {isExpanded ? (
            <ChevronUp size={16} color={caretColor} />
          ) : (
            <ChevronDown size={16} color={caretColor} />
          )}
        </div>
      </div>
    </div>
  );
}
